import { Controller } from "@hotwired/stimulus"

// The viewer modal's data rail (components/viewer.css, "Floating modal"). Closed,
// the document has the stage and its key facts and checks float beside it as a
// card; open, the rail holds the full form for editing and review. The choice is
// remembered in this browser, so moving through a workflow keeps it.
const KEY = "paperwork.viewer.railOpen"
// The ground around a document: the stage and the empty wrappers a PDF's pages
// and an email's letter sit in. Only a click that lands on one of these closes
// the viewer; a cell, a line of text or anything else on the stage never does.
const GROUND = [
  ".viewer-stage", "[data-controller~='pdf-review']", "[data-pdf-review-target='viewerContainer']",
  "[data-controller~='pdf-viewer']", "[data-pdf-viewer-target='viewerContainer']", ".page-container", ".mail-stage"
].join(", ")

// A field's value as a person sees it: what is checked, what is picked, what is typed.
const FIELDS = "textarea, input, select"
function fieldValue(field) {
  if (field.type === "checkbox" || field.type === "radio") return field.checked
  if (field.tagName === "SELECT") return [...field.options].map((option) => option.selected).join()
  return field.value
}

export default class extends Controller {
  connect() {
    this.apply(this.stored())
    // Each field's value from before a person first touches anything in the
    // viewer, so an edit counts however it was made: typed, checked, or set
    // by a picker into a hidden input.
    this.initial = new WeakMap()
    this.remember = this.remember.bind(this)
    this.element.addEventListener("pointerdown", this.remember, true)
    this.element.addEventListener("focusin", this.remember, true)
    // A saved edit or a sent answer is no longer at risk.
    this.forget = this.forget.bind(this)
    this.element.addEventListener("extracted-data-editor:field-updated", this.forget)
    this.element.addEventListener("turbo:submit-end", this.forget)
  }

  // Only the saved form or field row starts over: its fields take their saved
  // values as their new starting point. A draft elsewhere stays unsaved.
  forget(event) {
    if (event.type === "turbo:submit-end" && !event.detail?.success) return
    const scope = event.target
    if (!scope?.querySelectorAll) return
    const fields = scope.matches?.(FIELDS) ? [scope] : [...scope.querySelectorAll(FIELDS)]
    fields.forEach((field) => this.initial.set(field, fieldValue(field)))
  }

  remember(event) {
    if (event.type === "pointerdown") this.pressed = event.target
    this.element.querySelectorAll(FIELDS).forEach((field) => {
      if (!this.initial.has(field)) this.initial.set(field, fieldValue(field))
    })
  }

  toggle() {
    const opening = !this.element.classList.contains("is-rail-open")
    this.open(opening)
    // A review walks the rail's rows by keyboard; with the rail closed they are
    // hidden, so Enter, F, J and K would act on rows no one can see.
    if (!opening) this.endReview()
  }

  // Opening the rail from the card's All fields button.
  show() {
    this.open(true)
  }

  endReview() {
    const element = this.element.querySelector('[data-controller~="pdf-review"]')
    const review = element && this.application.getControllerForElementAndIdentifier(element, "pdf-review")
    review?.endReview?.()
  }

  open(value) {
    this.apply(value)
    try {
      localStorage.setItem(KEY, value ? "1" : "0")
    } catch (_error) {
      // Storage can be unavailable (private windows); the choice just isn't kept.
    }
  }

  apply(value) {
    this.element.classList.toggle("is-rail-open", value)
    this.element.querySelectorAll("[data-viewer-panel-pressed]").forEach((button) => {
      button.setAttribute("aria-pressed", value ? "true" : "false")
    })
  }

  // A task answered in the card (paperwork_tasks/_inline_action) and done: after a
  // short beat to read the confirmation, move on. The paperwork's last open
  // question moves to the next paperwork, the way the pager's › would; with more
  // questions left (the count on [data-viewer-task]), this paperwork reloads so
  // the card shows the next one. Nothing moves on a note, a stale answer or a
  // failure.
  answered(event) {
    const card = event.target.closest("[data-viewer-task]")
    if (!card) return
    const response = event.detail?.fetchResponse?.response
    if (!event.detail?.success || response?.headers?.get("X-Task-Action") !== "done") return

    const tape = card.querySelector(".state-tape")
    if (tape) {
      tape.textContent = "Done"
      tape.dataset.tone = "done"
    }

    const lastQuestion = (Number(card.dataset.viewerTask) || 1) <= 1
    clearTimeout(this.advanceTimer)
    this.advanceTimer = setTimeout(() => {
      if (lastQuestion) {
        const next = this.element.querySelector('[data-action~="attachments-modal#next"]:not([disabled])')
        if (next) next.click()
      } else {
        this.showNextQuestion()
      }
    }, 900)
  }

  showNextQuestion() {
    const frame = this.element.closest("turbo-frame")
    if (!frame) return
    if (frame.src) {
      frame.reload()
    } else {
      const src = this.element.closest("[data-frame-modal-src-value]")?.dataset.frameModalSrcValue
      if (src) frame.src = src
    }
  }

  // A click on the dark ground around the document closes the viewer, like a
  // click outside any modal (GROUND). It must start and end on that same
  // ground, so a drag out of the page or a text selection never closes it,
  // nor a click on a scrollbar, nor anything while an answer or edit is unsaved.
  backdrop(event) {
    if (event.button !== 0 || event.defaultPrevented) return
    const target = event.target
    if (!(target instanceof HTMLElement) || !target.matches(GROUND) || !target.closest(".viewer-stage")) return
    if (this.pressed !== target) return
    const box = target.getBoundingClientRect()
    if (event.clientX - box.left > target.clientLeft + target.clientWidth || event.clientY - box.top > target.clientTop + target.clientHeight) return
    if (!window.getSelection()?.isCollapsed) return
    if (this.unsaved()) return

    const modal = this.element.closest('[data-controller~="frame-modal"]')
    this.application.getControllerForElementAndIdentifier(modal, "frame-modal")?.close()
  }

  // Any field whose value moved since it was first seen. A search field
  // filters what shows; it holds nothing to lose.
  unsaved() {
    return [...this.element.querySelectorAll(FIELDS)].some((field) =>
      field.type !== "search" && this.initial.has(field) && fieldValue(field) !== this.initial.get(field))
  }

  disconnect() {
    clearTimeout(this.advanceTimer)
    this.element.removeEventListener("pointerdown", this.remember, true)
    this.element.removeEventListener("focusin", this.remember, true)
    this.element.removeEventListener("extracted-data-editor:field-updated", this.forget)
    this.element.removeEventListener("turbo:submit-end", this.forget)
  }

  stored() {
    try {
      return localStorage.getItem(KEY) === "1"
    } catch (_error) {
      return false
    }
  }
}
